export interface OidcConfig {
  issuerUrl: string;
  clientId: string;
}

export interface OidcSessionLike {
  sub?: string | null;
  email?: string | null;
  name?: string | null;
  preferredUsername?: string | null;
  activeOrg?: string | null;
  orgId?: string | null;
  org_id?: string | null;
  realmRoles?: string[] | string | null;
  role?: string[] | string | null;
}

export interface OidcClientLike {
  getSession(): Promise<OidcSessionLike | null>;
  login(returnTo: string): Promise<void>;
  logout(returnTo: string): Promise<void>;
}

export interface OidcFactoryArgs {
  config: OidcConfig;
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
  location?: Location;
}

export type OidcFactory = (args: OidcFactoryArgs) => OidcClientLike;

export const defaultOidcFactory: OidcFactory = (args) =>
  createBffClient(args.apiBaseUrl, args.fetchImpl, args.location);

export function getReturnTo(location: Location = window.location): string {
  return location.href;
}

export class OidcSessionRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`[auth-react] session request failed (${status}): ${body}`);
    this.name = "OidcSessionRequestError";
  }
}

function createBffClient(
  apiBaseUrl: string,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
  location: Location = window.location,
): OidcClientLike {
  const baseUrl = normalizeApiBaseUrl(apiBaseUrl, location);

  return {
    async getSession(): Promise<OidcSessionLike | null> {
      const res = await fetchImpl(`${baseUrl}/api/auth/me`, {
        method: "GET",
        headers: { accept: "application/json" },
        credentials: "include",
      });

      if (res.status === 401) {
        return null;
      }

      if (!res.ok) {
        const body = await res.text();
        throw new OidcSessionRequestError(res.status, body);
      }

      return (await res.json()) as OidcSessionLike;
    },

    async login(returnTo: string): Promise<void> {
      location.assign(buildAuthUrl(baseUrl, "/api/auth/login", returnTo));
    },

    async logout(returnTo: string): Promise<void> {
      location.assign(buildAuthUrl(baseUrl, "/api/auth/logout", returnTo));
    },
  };
}

function normalizeApiBaseUrl(apiBaseUrl: string, location: Location): string {
  const trimmed = apiBaseUrl.replace(/\/+$/, "");
  return trimmed.length > 0 ? trimmed : location.origin;
}

function buildAuthUrl(baseUrl: string, path: "/api/auth/login" | "/api/auth/logout", returnTo: string): string {
  const url = new URL(path, `${baseUrl}/`);
  url.searchParams.set("returnUrl", returnTo);
  return url.toString();
}
